import {
  ISSUE_STATUSES,
  ISSUE_STATUS_TONE,
  type Issue,
  type IssueStatus,
} from '../lib/issuesApi'
import { shopDate, shopDateTime } from '../lib/grouping'
import LinkList from './LinkList'
import DeleteButton from './DeleteButton'

interface Props {
  issue: Issue
  /** Needed by LinkList, which calls the database directly. */
  token: string
  isAdmin: boolean
  expanded: boolean
  busy: boolean
  onToggle: () => void
  onStatus: (issue: Issue, status: IssueStatus) => void
  onArchive: (issue: Issue) => void
  /** Admin only. Permanent — the card wraps this in a confirmation. */
  onDelete: (issue: Issue) => void | Promise<void>
  onTriage: (issue: Issue) => void
  onResolution: (issue: Issue) => void
  onMakeTask: (issue: Issue) => void
  /** Admin only: rank 1–3 for the next meeting, or null to clear. */
  onRank: (issue: Issue, rank: number | null) => void
  /** Fix the reporter's own words. Shown to the reporter and to admins. */
  onEditText: (issue: Issue) => void
  onLinksChanged: () => void
  /**
   * Desktop row layout — adds `card--row`, nothing more. Every rule behind
   * it sits inside the desktop media query in styles.css, so a phone renders
   * the same markup the same way whether this is set or not.
   */
  compact?: boolean
}

/**
 * One issue.
 *
 * Collapsed it is a scannable line — who, when, where, how urgent. Everything
 * the reporter typed lives behind a tap, because on a phone a list where every
 * card carries a full description is a list nobody scrolls to the bottom of.
 *
 * The header is the toggle, and the controls are siblings rather than children
 * of it: a button cannot legally contain other buttons.
 */
export default function IssueCard({
  issue,
  token,
  isAdmin,
  expanded,
  busy,
  onToggle,
  onStatus,
  onArchive,
  onDelete,
  onTriage,
  onResolution,
  onMakeTask,
  onRank,
  onEditText,
  onLinksChanged,
  compact = false,
}: Props) {
  const solved = issue.status === 'Solved'
  // A rank is a claim on the next meeting's time; only an open issue has one.
  const rankable = isAdmin && !solved && !issue.archived

  // Only worth showing separately when the title did not already capture it —
  // the title is derived from the first line of the description, so for a
  // one-line report the two are the same string.
  const bodyDiffers = issue.description.trim() !== issue.title.trim()

  const where = [issue.location, issue.department].filter(Boolean).join(' · ')

  // The reporter can fix their own account of what happened; admins can too.
  // Everyone else reads it. The database re-checks either way.
  const canEditText = issue.mine || isAdmin

  return (
    <article
      className={
        'card issue' +
        (compact ? ' card--row' : '') +
        (issue.urgency === 'High' && !solved ? ' card--overdue' : '') +
        (solved || issue.archived ? ' card--done' : '')
      }
    >
      <button
        type="button"
        className="issue__head"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="issue__headline">
          {issue.rank != null && (
            <span className="issue__rank" aria-label={`Ranked ${issue.rank}`}>
              {issue.rank}
            </span>
          )}
          <span className="card__title">{issue.title}</span>
          <span className="card__badges">
            {issue.archived && <span className="chip chip--low">Archived</span>}
            {issue.urgency !== 'Medium' && (
              <span className={`chip chip--${issue.urgency.toLowerCase()}`}>
                {issue.urgency}
              </span>
            )}
          </span>
        </span>

        <span className="issue__meta">
          {issue.mine ? 'You' : issue.reported_by_name}
          {' · '}
          Created {shopDate(issue.created_at)}
          {where ? ` · ${where}` : ''}
          {issue.follow_up_required && !solved ? ' · follow-up asked' : ''}
          {issue.task_count > 0
            ? ` · ${issue.task_count} ${issue.task_count === 1 ? 'task' : 'tasks'}`
            : ''}
        </span>

        <span className="issue__caret" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <div className="issue__body">
          {issue.headline_body && (
            <p className="card__brief card__brief--from">
              Dropped down from the headline “{issue.headline_body}”
            </p>
          )}

          {issue.task_title && (
            <p className="card__brief card__brief--from">
              Raised from the to-do “{issue.task_title}”
            </p>
          )}

          {bodyDiffers && <p className="issue__text">{issue.description}</p>}

          {issue.occurred_at && (
            <p className="issue__field">
              <span className="issue__key">Happened</span>
              {shopDateTime(issue.occurred_at)}
            </p>
          )}
          {issue.impact && (
            <p className="issue__field">
              <span className="issue__key">Impact</span>
              {issue.impact}
            </p>
          )}
          {issue.proposed_solution && (
            <p className="issue__field">
              <span className="issue__key">Suggested</span>
              {issue.proposed_solution}
            </p>
          )}
          {issue.follow_up_required && (
            <p className="issue__field">
              <span className="issue__key">Follow-up</span>
              {issue.mine ? 'You asked' : `${issue.reported_by_name} asked`} to be
              followed up with.
            </p>
          )}
          {issue.resolution_notes && (
            <p className="issue__field issue__field--out">
              <span className="issue__key">Outcome</span>
              {issue.resolution_notes}
            </p>
          )}

          {/* The reporter and admins can attach; everyone else just sees them.
              The database re-checks with can_link either way. */}
          <LinkList
            token={token}
            links={issue.links ?? []}
            canEdit={issue.mine || isAdmin}
            issueId={issue.id}
            onChanged={onLinksChanged}
          />

          {isAdmin && (
            <div className="seg" role="group" aria-label="Issue status">
                {ISSUE_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={
                      'seg__btn' +
                      (issue.status === s ? ` seg__btn--on seg__btn--${ISSUE_STATUS_TONE[s]}` : '')
                    }
                    aria-pressed={issue.status === s}
                    disabled={busy}
                    onClick={() => {
                      if (issue.status !== s) onStatus(issue, s)
                    }}
                  >
                    {s}
                  </button>
                ))}
            </div>
          )}

          {/* EOS pick-1-2-3. Tapping the rank it already has clears it. The
              list puts ranked issues first, and the database takes the rank
              away from whichever issue held it. */}
          {rankable && (
            <div className="issue__rankrow">
              <span className="issue__key">Rank for the meeting</span>
              <div className="seg seg--rank" role="group" aria-label="Rank for the meeting">
                {[1, 2, 3].map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={'seg__btn' + (issue.rank === r ? ' seg__btn--on' : '')}
                    aria-pressed={issue.rank === r}
                    disabled={busy}
                    onClick={() => onRank(issue, issue.rank === r ? null : r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          {canEditText && (
            <div className="card__actions">
              {/* Fixing your own words. Separate from Reclassify, which is the
                  admin's control over title/area/urgency and cannot touch
                  what the reporter actually wrote. */}
              <button
                type="button"
                className="btn btn--quiet"
                disabled={busy}
                onClick={() => onEditText(issue)}
              >
                Edit
              </button>

              {isAdmin && (
                <>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => onMakeTask(issue)}
                  >
                    Create a task
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => onResolution(issue)}
                  >
                    {issue.resolution_notes ? 'Edit outcome' : 'Add outcome'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => onTriage(issue)}
                  >
                    Reclassify
                  </button>
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={busy}
                    onClick={() => onArchive(issue)}
                  >
                    {issue.archived ? 'Restore' : 'Archive'}
                  </button>
                  <DeleteButton
                    noun="issue"
                    disabled={busy}
                    consequence={
                      `“${issue.title}” will be removed` +
                      (issue.links.length > 0
                        ? `, along with its ${issue.links.length === 1 ? 'link' : `${issue.links.length} links`}`
                        : '') +
                      '. ' +
                      (issue.task_count > 0
                        ? `The ${issue.task_count === 1 ? 'task' : `${issue.task_count} tasks`} made from it will stay, but lose the link back to this issue.`
                        : 'Nothing else is affected.')
                    }
                    onDelete={() => onDelete(issue)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  )
}
